import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { inr } from "@/lib/api";
import { PageHeader, Panel, Loading, Pill, StatCard } from "@/components/kit";
import OrgDateFilter from "@/components/OrgDateFilter";
import { Button } from "@/components/ui/button";
import { Banknote, ScrollText } from "lucide-react";

const EMPTY = { from_date: "", to_date: "", opco_id: "", region_id: "", rso_id: "", ao_id: "", dealer_id: "", status: "" };

export default function Settlements() {
  const qc = useQueryClient();
  const [filters, setFilters] = useState(EMPTY);
  const { data, isLoading } = useQuery({
    queryKey: ["settlements", filters],
    queryFn: async () => (await api.get("/settlements", { params: Object.fromEntries(Object.entries(filters).filter(([, x]) => x)) })).data,
    placeholderData: (p) => p,
  });
  const act = useMutation({
    mutationFn: async ({ id, action }) => (await api.patch(`/settlements/${id}`, { action })).data,
    onSuccess: (d) => { toast.success(`Settlement ${d.status}`); qc.invalidateQueries({ queryKey: ["settlements"] }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Action failed"),
  });

  if (isLoading || !data) return <Loading label="Loading settlements…" />;
  const total = data.settlements.reduce((a, s) => a + s.net, 0);

  return (
    <div>
      <PageHeader title="Settlement & Credit-Note Module" subtitle="Disbursal batches, TDS, credit notes and ERP reconciliation (integration-ready)" />
      <OrgDateFilter value={filters} onChange={setFilters}>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Status</div>
          <select className="h-9 rounded-md border border-slate-200 px-2.5 bg-white text-sm w-full" data-testid="settle-filter-status"
            value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}>
            <option value="">All Status</option>
            <option value="settled">Settled</option>
            <option value="pending">Pending</option>
            <option value="hold">Hold</option>
          </select>
        </div>
      </OrgDateFilter>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <StatCard testid="settle-kpi-net" label="Total Net" value={inr(total)} icon={Banknote} tone="navy" />
        <StatCard testid="settle-kpi-reconciled" label="Reconciled (Settled)" value={inr(data.reconciled_net)} icon={ScrollText} tone="emerald" />
        <StatCard testid="settle-kpi-count" label="Records" value={data.settlements.length} icon={ScrollText} tone="gold" />
      </div>

      <Panel testid="settlements-panel">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-400 text-xs uppercase tracking-wider border-b border-slate-100">
                <th className="py-2.5 pr-4">Credit Note</th><th className="py-2.5 pr-4">Dealer</th>
                <th className="py-2.5 pr-4 text-right">Gross</th><th className="py-2.5 pr-4 text-right">TDS</th>
                <th className="py-2.5 pr-4 text-right">Net</th><th className="py-2.5 pr-4">Mode</th>
                <th className="py-2.5 pr-4">ERP</th><th className="py-2.5 pr-4">Status</th>
                <th className="py-2.5 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {data.settlements.map((s, i) => (
                <tr key={s.id} className="drill-row border-b border-slate-50" data-testid={`settle-row-${i}`}>
                  <td className="py-3 pr-4 font-mono text-xs text-slate-500">{s.credit_note_no}</td>
                  <td className="py-3 pr-4 font-medium text-slate-800">{s.dealer_name}</td>
                  <td className="py-3 pr-4 text-right text-slate-600">{inr(s.gross)}</td>
                  <td className="py-3 pr-4 text-right text-red-500">{inr(s.tds)}</td>
                  <td className="py-3 pr-4 text-right font-semibold text-[#00529B]">{inr(s.net)}</td>
                  <td className="py-3 pr-4 text-slate-500 text-xs">{s.mode}</td>
                  <td className="py-3 pr-4"><Pill tone={s.erp_sync}>{s.erp_sync.replace("_", " ")}</Pill></td>
                  <td className="py-3 pr-4"><Pill tone={s.status}>{s.status}</Pill></td>
                  <td className="py-3 text-right">
                    <div className="flex justify-end gap-1.5">
                      {s.status !== "settled" && (
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" data-testid={`settle-btn-${i}`}
                          onClick={() => act.mutate({ id: s.id, action: "settle" })}>Settle</Button>
                      )}
                      {s.status !== "hold" && s.status !== "settled" && (
                        <Button size="sm" variant="outline" data-testid={`hold-btn-${i}`}
                          onClick={() => act.mutate({ id: s.id, action: "hold" })}>Hold</Button>
                      )}
                      {s.status === "hold" && (
                        <Button size="sm" variant="outline" data-testid={`release-btn-${i}`}
                          onClick={() => act.mutate({ id: s.id, action: "release" })}>Release</Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}
